import { getCookie, setCookie, getCart, setCartAttributes } from "@theme/utilities";
import { WebSourceCodesSet } from "@theme/web-source-code.util";

/**
 * Process tracking codes from URL parameters and sync with cart
 * @param {string | null | undefined} royaltyCodeFromMetafields
 * @returns {Promise<void>}
 */
export async function processTrackingCodes(royaltyCodeFromMetafields) {
  const namespace = "TrackingCodes";
  const currentUrl = new URL(window.location.href);

  const webSourceCodeCookie = "bodi-web-source-code";
  const royaltyCodeCookie = "bodi-royalty-code";
  const cookieMaxAge = 30 * 24 * 60 * 60; // 30 days
  const webSourceCodeCartAttributeName = "webSourceCode";
  const codeParam = currentUrl.searchParams.get("code");
  const rcParam = currentUrl.searchParams.get("rc");

  const royaltyCode = rcParam || royaltyCodeFromMetafields;

  const existingWebSourceCodeCookie = getCookie(webSourceCodeCookie);
  const existingRoyaltyCodeCookie = getCookie(royaltyCodeCookie);

  /**
   * Set the web source codes to the cart
   * @param {string} webSourceCodes
   */
  async function setWebSourceCodesToCart(webSourceCodes) {
    try {
      console.info(`[${namespace}] - Setting web source codes to cart`, { webSourceCodes });
      await setCartAttributes({ [webSourceCodeCartAttributeName]: webSourceCodes });
      console.info(`[${namespace}] - Web source codes set to cart successfully`, {
        webSourceCodes,
      });
    } catch (error) {
      console.error(`[${namespace}] - Set web source codes to cart failed`, {
        webSourceCodes,
        error,
      });
    }
  }

  /**
   * if the code parameter is present in the URL,
   * 1. add it to the web source codes list
   * 2. update the cookie
   * 3. update the cart with the web source codes
   */
  if (codeParam) {
    console.info(`[${namespace}] - Code parameter found in URL`, { codeParam });

    const webSourceCodes = new WebSourceCodesSet(existingWebSourceCodeCookie);
    webSourceCodes.add(codeParam);
    const serializedCodes = webSourceCodes.serialize();

    setCookie(webSourceCodeCookie, serializedCodes, { maxAge: cookieMaxAge });

    await setWebSourceCodesToCart(serializedCodes);
  }

  // update royalty code to GEN on homepage
  const isHomepage = Theme?.template?.name === "index";
  if (isHomepage) {
    console.info(`[${namespace}] - Setting royalty code to GEN`);
    setCookie("bodi-royalty-code", "GEN", {
      maxAge: 30 * 24 * 60 * 60, // 30 days
      sameSite: "lax",
    });
  }

  /**
   * if the rc parameter is present in the URL,
   * 1. update the cookie
   * 2. update the cart with the royalty code
   * 3. add the royalty code to the line item properties
   */
  if (royaltyCode) {
    if (rcParam) {
      console.info(`[${namespace}] - Royalty code parameter found in URL`, { rcParam });
    }
    if (royaltyCodeFromMetafields) {
      console.info(`[${namespace}] - Royalty code found in metafields`, {
        royaltyCodeFromMetafields,
      });
    }

    setCookie(royaltyCodeCookie, royaltyCode, { maxAge: cookieMaxAge });
  }

  // If we processed URL parameters, return early
  if (codeParam || royaltyCode) {
    return;
  }

  /**
   * otherwise, if the web source codes are present in the cookie and are different from the cart,
   * 1. update the cookie
   * 2. update the cart with the web source codes
   */
  const cart = await getCart();

  if (existingWebSourceCodeCookie) {
    console.info(`[${namespace}] - Web source codes found in cookie`, {
      existingWebSourceCodeCookie,
    });
    if (existingWebSourceCodeCookie !== cart.attributes[webSourceCodeCartAttributeName]) {
      await setWebSourceCodesToCart(existingWebSourceCodeCookie);
    } else {
      console.info(`[${namespace}] - Web source codes already set to cart`, {
        existingWebSourceCodeCookie,
      });
    }
  }
}

(async function TrackingCodesModule() {
  // skip /pages/checkout-start page, it will call processTrackingCodes explicitly
  if (window.location.pathname.includes("/pages/checkout-start")) {
    return;
  }

  const royaltyCodeFromMetafields = window.BODi.royaltyCode;

  await processTrackingCodes(royaltyCodeFromMetafields);
})();
